import { Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInsightsContext } from "@/contexts/insights-context";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocation } from "wouter";
import { useSidebar } from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

function getLocationInitials(label: string): string {
  return label.slice(0, 2).toUpperCase();
}

export function InsightsLocationNav() {
  const { selectedShelter, setSelectedShelter } = useInsightsContext();
  const { locations, isLoading } = useActiveLocations();
  const [currentPath] = useLocation();
  const { collapsed, isMobile } = useSidebar();
  
  const isCollapsed = collapsed && !isMobile;
  const isPopulationPage = currentPath === "/population" || currentPath.startsWith("/population?");
  const sectionTitle = "Locations";

  if (isLoading) {
    return (
      <div className={cn(
        "shrink-0 border-r bg-background p-3 space-y-2 transition-all duration-200 hidden md:block",
        isCollapsed ? "w-16" : "w-40"
      )}>
        {!isCollapsed && <Skeleton className="h-6 w-24 mb-4" />}
        {!isCollapsed && <Skeleton className="h-4 w-16 mb-2" />}
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className={cn("h-9", isCollapsed ? "w-10" : "w-full")} />
        ))}
      </div>
    );
  }

  const navItems = [
    { code: "system", label: "System", isSystem: true },
    ...locations.map(loc => ({ code: loc.code, label: loc.label, isSystem: false })),
  ];

  return (
    <nav 
      className={cn(
        "shrink-0 border-r bg-background hidden md:flex md:flex-col transition-all duration-200",
        isCollapsed ? "w-16" : "w-40"
      )}
      aria-label="Insights navigation"
      data-testid="nav-insights-locations"
    >
      {!isCollapsed && (
        <div className="px-4 pt-8 pb-3 border-b">
          <h1 className="text-lg font-semibold" data-testid="text-section-title">
            {sectionTitle}
          </h1>
        </div>
      )}
      
      <div className={cn("flex-1", isCollapsed ? "p-2 pt-4" : "p-3")}>
        {!isCollapsed && (
          <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 px-2">
            View
          </h2>
        )}
        <ul className="space-y-1">
          {navItems.map((item) => {
            const isActive = selectedShelter === item.code;
            
            return (
              <li key={item.code}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setSelectedShelter(item.code)}
                      className={cn(
                        "w-full flex items-center rounded-md transition-colors",
                        isCollapsed ? "justify-center p-2" : "gap-2 px-3 py-2",
                        isActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-muted-foreground hover-elevate"
                      )}
                      data-testid={`button-shelter-${item.code.toLowerCase()}`}
                      data-active={isActive}
                    >
                      {item.isSystem ? (
                        <Globe className={cn(
                          "h-4 w-4 shrink-0",
                          isActive ? "text-primary" : "text-muted-foreground"
                        )} />
                      ) : (
                        <Avatar className="h-6 w-6 shrink-0">
                          <AvatarFallback className={cn(
                            "text-[10px] font-medium",
                            isActive 
                              ? "bg-primary text-primary-foreground" 
                              : "bg-muted text-muted-foreground"
                          )}>
                            {getLocationInitials(item.label)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      {!isCollapsed && (
                        <span className="truncate text-sm">{item.label}</span>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="center" hidden={!isCollapsed}>
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}

export function InsightsLocationNavMobile() {
  const { selectedShelter, setSelectedShelter } = useInsightsContext();
  const { locations, isLoading } = useActiveLocations();

  if (isLoading) {
    return (
      <div className="md:hidden pb-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-16" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-8 shrink-0 rounded-full" />
          ))}
        </div>
      </div>
    );
  }

  const navItems = [
    { code: "system", label: "System", isSystem: true },
    ...locations.map(loc => ({ code: loc.code, label: loc.label, isSystem: false })),
  ];

  return (
    <div className="md:hidden pb-2" data-testid="nav-insights-locations-mobile">
      {/* Location selector: "Location:" label + icons (selected shows full name in blue) */}
      <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4">
        <span className="text-sm font-medium text-muted-foreground shrink-0">Location:</span>
        {navItems.map((item) => {
          const isActive = selectedShelter === item.code;
          
          // Active item shows full label in blue oval pill with white text
          if (isActive) {
            return (
              <button
                key={item.code}
                onClick={() => setSelectedShelter(item.code)}
                className="shrink-0 flex items-center gap-1.5 bg-primary text-primary-foreground font-medium text-sm px-3 py-1 rounded-full transition-colors"
                data-testid={`button-shelter-mobile-${item.code.toLowerCase()}`}
                data-active={isActive}
              >
                {item.isSystem && <Globe className="h-4 w-4" />}
                {item.label}
              </button>
            );
          }
          
          // Inactive items show circle icons
          return (
            <button
              key={item.code}
              onClick={() => setSelectedShelter(item.code)}
              className={cn(
                "shrink-0 flex items-center justify-center transition-colors",
                item.isSystem ? "w-8 h-8 rounded-full" : "",
                "text-muted-foreground hover-elevate"
              )}
              data-testid={`button-shelter-mobile-${item.code.toLowerCase()}`}
              data-active={isActive}
              title={item.label}
            >
              {item.isSystem ? (
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-muted text-muted-foreground">
                  <Globe className="h-4 w-4" />
                </div>
              ) : (
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="text-[11px] font-medium bg-muted text-muted-foreground">
                    {getLocationInitials(item.label)}
                  </AvatarFallback>
                </Avatar>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
